import { useItem } from 'dnd-timeline';
import { Bike, CircleCheck, CircleDashed, TriangleAlert } from 'lucide-react';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placePopover } from '../../../components/ui/popoverPlacement.js';
import { copyId } from '../grouping.js';
import { BAR_PADDING } from '../scheduleDensity.js';
import type { ScheduleBar } from '../types.js';
import { ScheduleBarCard, isBookingBar, shortRef } from './ScheduleBarCard.js';
import { barStyleFor, needsAttention } from './barStyle.js';
import { mytPeriod } from './dropLogic.js';

/** How long the pointer rests on a bar before its card opens (a passing pointer never flickers one). */
const CARD_SHOW_DELAY_MS = 300;

/** 407: "3:00 PM", Malaysia time: when the car's next booking starts. */
function nextBookingTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

export interface BarItemProps {
  bar: ScheduleBar;
  carLabel: string;
  /** The span drawn: the bar's own start and end (dnd-timeline pads content past the view edge). */
  span: { start: number; end: number };
  /** An extension leg that starts exactly where its rental's previous bar ends. */
  joinedStart: boolean;
  groupKey: string | null;
  /** No drag at all (the caller may not move this bar). */
  disabled: boolean;
  /** 316 T022/T024: both edges may be dragged to change the length. */
  resizable?: boolean;
  /** 396: the bar's own rectangle comes with it, so a car drawn twice anchors its card to the copy clicked. */
  onOpenBar?: (bar: ScheduleBar, rect?: DOMRect) => void;
  /** 396 (#1342): grouped by host, the group this copy is drawn in; its item id carries it. */
  copyKey?: string;
  /** 396 (mock-up screen 3): this booking is being dragged in another copy of the car. */
  twin?: boolean;
}

/**
 * 316 (US3) — one bar on the timeline.
 *
 * The visible text is what fits; the accessible name carries everything, so nothing depends on
 * width (096 FR-008). Below about 3.5 rem the text is hidden entirely rather than truncated to a
 * lone character (FR-018); the card still has it all.
 */
/**
 * Memoised (SC-007): the grid re-renders when a drag's proposal changes, and a bar whose own props
 * did not change must not redo its work.
 */
export const BarItem = memo(function BarItem({
  bar,
  carLabel,
  span,
  joinedStart,
  groupKey,
  disabled,
  resizable = false,
  onOpenBar,
  copyKey,
  twin = false,
}: BarItemProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const itemId = copyId(bar.id, copyKey);
  const { setNodeRef, attributes, listeners, itemStyle, itemContentStyle, isDragging } = useItem({
    id: itemId,
    span,
    disabled: disabled && !resizable,
    data: { bar },
    // Only a resizable bar has edge handles: dnd-timeline treats a press within half this width of
    // either edge as a resize, which for a non-resizable short bar would be the whole bar.
    resizeHandleWidth: resizable ? 12 : 0,
    // dnd-timeline stretches the node's inline style while resizing. The change is only PROPOSED
    // (confirmed in a popover, or cancelled), so the bar returns to its real span on release; a
    // confirmed save redraws it from the refetched schedule.
    onResizeEnd: () => {
      const el = nodeRef.current;
      if (!el) return;
      el.style.width = `${itemStyle.width}px`;
      el.style.left = `${itemStyle.left as number}px`;
    },
  });
  const style = barStyleFor(bar);
  const booking = isBookingBar(bar);
  const ref = booking && bar.booking_id ? shortRef(bar.booking_id) : null;
  const period = mytPeriod(Date.parse(bar.start_at), Date.parse(bar.end_at));
  const paidWord =
    booking && bar.money_settled !== undefined ? (bar.money_settled ? 'Paid' : 'Not paid') : null;

  const isLegacyExtension = bar.kind === 'extension';
  const name = [
    carLabel,
    isLegacyExtension ? 'Extension' : null,
    `${style.word}${bar.overdue ? ' · Overdue' : ''}`,
    bar.overdue && bar.next_booking_at
      ? `Next booking ${nextBookingTime(bar.next_booking_at)}`
      : null,
    bar.car_not_back ? 'Car not back yet' : null,
    // 416 (#1431): said in words, like Overdue.
    bar.outside_zone ? 'Outside zone' : null,
    ref ? `Booking ${ref}` : null,
    bar.root_booking_id ? `Extension of booking ${shortRef(bar.root_booking_id)}` : null,
    bar.customer_name ?? null,
    paidWord,
    // 361: said in words, not only by the mark.
    bar.trip?.food_delivery ? 'OK to do food delivery' : null,
    period,
    bar.reason,
    bar.continues_before ? 'continues from before this view' : null,
    bar.continues_after ? 'continues after this view' : null,
    onOpenBar && booking ? 'Shows the booking details' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const [cardOpen, setCardOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        nodeRef.current = el;
      }}
      style={itemStyle}
      {...listeners}
      {...attributes}
      data-testid={`schedule-bar-${itemId}`}
      data-twin={twin ? 'true' : undefined}
      data-display-state={style.state}
      data-kind={bar.kind}
      data-group={groupKey ?? undefined}
      data-joined-start={joinedStart ? 'true' : undefined}
      data-paid={paidWord ? String(bar.money_settled) : undefined}
      data-overdue={String(bar.overdue)}
      data-continues-before={String(bar.continues_before)}
      data-continues-after={String(bar.continues_after)}
      aria-label={name}
      className={[
        BAR_PADDING,
        isDragging ? 'opacity-40' : '',
        // 396: the same booking moving in another copy of the car: a dashed outline, as the mock-up.
        twin ? 'rounded-md outline-2 outline-dashed outline-brand-yellow' : '',
      ].join(' ')}
      onClick={
        onOpenBar
          ? () => onOpenBar(bar, nodeRef.current?.getBoundingClientRect() ?? undefined)
          : undefined
      }
      // 🔴 `listeners` carries the keyboard sensor's own `onKeyDown` (Space starts a drag). A bare
      // `onKeyDown` here replaced it, so keyboard dragging silently did nothing (found at T031 in a
      // browser; every pointer test passed). Both run: the sensor first, then Enter opens the bar
      // unless a keyboard drag is in progress, where Enter ends the drag instead.
      onKeyDown={(e) => {
        (listeners as { onKeyDown?: (event: React.KeyboardEvent) => void }).onKeyDown?.(e);
        if (onOpenBar && e.key === 'Enter' && !isDragging) {
          onOpenBar(bar, nodeRef.current?.getBoundingClientRect() ?? undefined);
        }
      }}
      onFocus={() => setCardOpen(true)}
      onBlur={() => {
        clearTimer();
        setCardOpen(false);
      }}
      onPointerEnter={() => {
        clearTimer();
        timer.current = setTimeout(() => setCardOpen(true), CARD_SHOW_DELAY_MS);
      }}
      onPointerLeave={() => {
        clearTimer();
        setCardOpen(false);
      }}
    >
      <div style={itemContentStyle}>
        <div
          className={[
            '@container flex h-full w-full min-w-0 items-center gap-1 overflow-hidden px-1.5 text-xs leading-tight',
            style.className,
            joinedStart ? 'rounded-r' : 'rounded',
            bar.continues_before ? 'rounded-l-none' : '',
            bar.continues_after ? 'rounded-r-none' : '',
            bar.overdue ? 'ring-2 ring-semantic-warning-text font-semibold' : '',
            onOpenBar ? 'cursor-pointer' : '',
          ].join(' ')}
        >
          {bar.continues_before && <span aria-hidden="true">‹</span>}
          {/* #631: status marks that do not depend on colour. The words are in the accessible name
              and the card; these are for the eye scanning a crowded Month view. */}
          {needsAttention(bar) && (
            <TriangleAlert
              data-testid={`schedule-bar-attention-${bar.id}`}
              aria-hidden="true"
              size={12}
              className="hidden shrink-0 @min-[1.75rem]:inline-block"
            />
          )}
          <span className="hidden min-w-0 flex-1 truncate @min-[3.5rem]:block">
            {/* Said in words, not only by the ring: late back is what someone acts on today. */}
            {bar.overdue && (
              <span className="font-semibold">
                Overdue
                {/* 407 (#1370): the car's next booking it affects (labels only; nothing moves). */}
                {bar.next_booking_at
                  ? ` · next booking ${nextBookingTime(bar.next_booking_at)}`
                  : ''}
                {' · '}
              </span>
            )}
            {bar.car_not_back && <span className="font-semibold">Car not back yet · </span>}
            {bar.outside_zone && (
              <span data-testid={`schedule-bar-outside-${bar.id}`} className="font-semibold">
                Outside zone ·{' '}
              </span>
            )}
            {isLegacyExtension && <span>Extension · </span>}
            {booking ? (
              <>
                {/* 349 (#951): an unpaid booking says so on its face, not only by its faded style. */}
                {style.state === 'awaiting_payment' && <span>{style.word} · </span>}
                <span className="font-semibold">{ref}</span>
                {bar.customer_name ? ` · ${bar.customer_name}` : ''}
              </>
            ) : (
              <>
                {style.word}
                {bar.reason ? ` · ${bar.reason}` : ''}
              </>
            )}
          </span>
          {/* 361 (design 2d): a customer OK to do food delivery. Same rule and same outlined style as Paid: the
              icon on narrow bars and the word only from 11rem (later than Paid's 7rem, so a medium bar keeps its booking
              reference: found in the browser check), in the bar's own text colour so it reads in both themes (a
              yellow fill has no text token that stays dark in dark mode). The non-halal answer is in the card. */}
          {bar.trip?.food_delivery && (
            <>
              <Bike
                data-testid={`schedule-bar-delivery-icon-${bar.id}`}
                aria-hidden="true"
                size={12}
                className="hidden shrink-0 @min-[2.5rem]:inline-block @min-[11rem]:hidden"
              />
              <span
                data-testid={`schedule-bar-delivery-word-${bar.id}`}
                className="hidden shrink-0 rounded-sm px-1 text-[10px] font-semibold uppercase ring-1 ring-current @min-[11rem]:inline"
              >
                Delivery
              </span>
            </>
          )}
          {/* Narrow bars get the mark; from 7rem the word takes its place. */}
          {paidWord &&
            (bar.money_settled ? (
              <CircleCheck
                data-testid={`schedule-bar-paid-icon-${bar.id}`}
                aria-hidden="true"
                size={12}
                className="hidden shrink-0 @min-[2.5rem]:inline-block @min-[7rem]:hidden"
              />
            ) : (
              <CircleDashed
                data-testid={`schedule-bar-unpaid-icon-${bar.id}`}
                aria-hidden="true"
                size={12}
                className="hidden shrink-0 @min-[2.5rem]:inline-block @min-[7rem]:hidden"
              />
            ))}
          {paidWord && (
            <span className="hidden shrink-0 rounded-sm px-1 text-[10px] font-semibold uppercase ring-1 ring-current @min-[7rem]:inline">
              {bar.money_settled ? 'Paid' : 'Unpaid'}
            </span>
          )}
          {bar.continues_after && <span aria-hidden="true">›</span>}
          {resizable && (
            <>
              <span
                data-testid={`schedule-resize-start-${bar.id}`}
                aria-hidden="true"
                className="absolute inset-y-1 left-0 w-1.5 cursor-ew-resize rounded-l bg-current opacity-40"
              />
              <span
                data-testid={`schedule-resize-end-${bar.id}`}
                aria-hidden="true"
                className="absolute inset-y-1 right-0 w-1.5 cursor-ew-resize rounded-r bg-current opacity-40"
              />
            </>
          )}
        </div>
      </div>
      {cardOpen && <BarCardPopover anchor={nodeRef} bar={bar} carLabel={carLabel} />}
    </div>
  );
});

function BarCardPopover({
  anchor,
  bar,
  carLabel,
}: {
  anchor: React.RefObject<HTMLElement | null>;
  bar: ScheduleBar;
  carLabel: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const a = anchor.current;
    const box = boxRef.current;
    if (!a || !box) return;
    const { width, height } = box.getBoundingClientRect();
    const p = placePopover(
      a.getBoundingClientRect(),
      { width, height },
      { width: window.innerWidth, height: window.innerHeight },
      4,
    );
    setPos({ left: p.left, top: p.top });
  }, [anchor]);
  return createPortal(
    <div
      ref={boxRef}
      role="tooltip"
      className="pointer-events-none z-50 rounded-md border border-hairline bg-canvas px-2.5 py-2 shadow-lg"
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <ScheduleBarCard bar={bar} carLabel={carLabel} />
    </div>,
    document.body,
  );
}
